// Per-file AI summary panel.
//
// Renders the four states of LegalContractFileAnalysis:
//   pending / processing - spinner + polling every ~4s
//   completed            - definition list of extracted fields
//   failed               - error banner + retry button
//
// Polling is local to this component (one analysis per file) so multiple
// files on the same contract page each maintain their own short-lived
// interval. The interval is torn down whenever status transitions to a
// terminal state (completed/failed) or the component unmounts.

import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faRotate,
  faWandSparkles,
} from '@fortawesome/free-solid-svg-icons';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { ContractReview } from './ContractReview';
import { legalContractService } from '../../../services/legalContractService';

const POLL_INTERVAL_MS = 4000;

// Display labels for the LegalContractExtractionSchema keys. Keeping
// this list in component code (not the backend serializer) so the
// presentation can evolve without API churn.
const FIELD_LABELS = [
  ['counterparty_name', 'Counterparty'],
  ['counterparty_address', 'Address'],
  ['product', 'Product'],
  ['volume', 'Volume'],
  ['delivery_location', 'Delivery Location'],
  ['laycan', 'Laycan'],
  ['payment_terms', 'Payment Terms'],
  ['pricing_basis', 'Pricing Basis'],
  ['governing_law', 'Governing Law'],
  ['arbitration', 'Arbitration'],
];

const formatExtractedAt = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return value;
  }
};

const FileSummaryPanel = ({ contractId, file, onAnalysisChange, canEdit = true }) => {
  const [analysis, setAnalysis] = useState(file?.analysis || null);
  const [retrying, setRetrying] = useState(false);
  const [pollError, setPollError] = useState(null);
  const pollTimerRef = useRef(null);

  // Sync the local analysis state when the parent passes a fresh file
  // (e.g. after a delete/upload reorders the list).
  useEffect(() => {
    setAnalysis(file?.analysis || null);
  }, [file]);

  // Polling effect — only runs while status is pending/processing.
  useEffect(() => {
    const status = analysis?.status;
    if (!status || (status !== 'pending' && status !== 'processing')) {
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await legalContractService.getFileAnalysis(
          contractId, file.id,
        );
        if (cancelled) return;
        setAnalysis(fresh);
        setPollError(null);
        if (onAnalysisChange) onAnalysisChange(file.id, fresh);
        if (fresh.status === 'completed' || fresh.status === 'failed') {
          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          return;
        }
      } catch (err) {
        if (cancelled) return;
        // Don't stop polling on transient errors — surface inline + try again
        setPollError(
          err?.response?.data?.detail
            || err?.message
            || 'Failed to refresh analysis status.',
        );
      }
      if (!cancelled) {
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    // Kick the first poll on the same delay as subsequent ones so we
    // don't double-fetch right after upload (the upload response
    // already gave us a fresh row).
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [analysis?.status, contractId, file.id, onAnalysisChange]);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    setPollError(null);
    try {
      const next = await legalContractService.retryFileAnalysis(
        contractId, file.id,
      );
      setAnalysis(next);
      if (onAnalysisChange) onAnalysisChange(file.id, next);
    } catch (err) {
      setPollError(
        err?.response?.data?.detail
          || err?.message
          || 'Retry failed. Please try again in a moment.',
      );
    } finally {
      setRetrying(false);
    }
  };

  if (!analysis) {
    // Defensive: a file should always have an analysis row from upload,
    // but if a legacy file is missing one show a friendly placeholder
    // rather than a crash.
    return (
      <div className="legal-summary legal-summary--pending">
        <FontAwesomeIcon icon={faWandSparkles} />
        <span>Summary not available for this file.</span>
      </div>
    );
  }

  if (analysis.status === 'pending' || analysis.status === 'processing') {
    return (
      <div className="legal-summary legal-summary--pending" aria-live="polite">
        <LoadingSpinner size="small" message="" />
        <div className="legal-summary__pending-text">
          <strong>Extracting key fields…</strong>
          <small>This usually takes 10–30 seconds.</small>
          {pollError && (
            <small className="legal-summary__pending-err">{pollError}</small>
          )}
        </div>
      </div>
    );
  }

  if (analysis.status === 'failed') {
    return (
      <div className="legal-summary legal-summary--failed" role="alert">
        <div className="legal-summary__failed-head">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <strong>Could not extract a summary, translation or comparison.</strong>
        </div>
        {analysis.error_message && (
          <div className="legal-summary__failed-body">
            {analysis.error_message}
          </div>
        )}
        {canEdit && (
          <button
            type="button"
            className="legal-summary__retry"
            onClick={handleRetry}
            disabled={retrying}
          >
            <FontAwesomeIcon icon={faRotate} spin={retrying} />
            {retrying ? 'Retrying…' : 'Retry analysis'}
          </button>
        )}
        {pollError && (
          <div className="legal-summary__failed-body" style={{ marginTop: 6 }}>
            {pollError}
          </div>
        )}
      </div>
    );
  }

  // status === 'completed'
  const extracted = analysis.extracted_data || {};
  const rows = FIELD_LABELS
    .map(([key, label]) => [label, extracted[key]])
    .filter(([, value]) => value !== null && value !== undefined && value !== '');
  const notable = Array.isArray(extracted.notable_clauses)
    ? extracted.notable_clauses
    : [];

  return (
    <div className="legal-summary legal-summary--completed">
      <div className="legal-summary__head">
        <FontAwesomeIcon icon={faCircleCheck} />
        <strong>File Summary</strong>
        <span className="legal-summary__head-meta">
          extracted {formatExtractedAt(analysis.completed_at)}
        </span>
      </div>
      {rows.length === 0 && notable.length === 0 ? (
        <div className="legal-summary__empty">
          No fields could be extracted from this document.
        </div>
      ) : (
        <>
          {rows.length > 0 && (
            <dl className="legal-summary__fields">
              {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
          {notable.length > 0 && (
            <div className="legal-summary__notable">
              <div className="legal-summary__notable-head">Notable clauses</div>
              <ul>
                {notable.map((clause, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={idx}>{clause}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <ContractReview analysis={analysis} />
    </div>
  );
};

export default FileSummaryPanel;
